/** Graph filter toolbar — edge types, category chips, implicit toggle, live search (GH-128) */

import { useState, useCallback, useEffect, useRef } from 'react'
import { RotateCcw, Search, X } from 'lucide-react'
import { EDGE_TYPES } from '@agentwiki/shared'
import { CATEGORY_COLORS, DEFAULT_NODE_COLOR } from './graph-canvas-styles'

export interface GraphFilters {
  category?: string
  tag?: string
  types?: string[]
  includeImplicit?: boolean
}

interface GraphToolbarProps {
  filters: GraphFilters
  onChange: (filters: GraphFilters) => void
  stats?: { nodeCount: number; edgeCount: number; explicitEdges: number; implicitEdges: number }
  searchValue?: string
  /**
   * Called when the search query changes. MUST be memoized by parent (useCallback)
   * to avoid debounce timer resets on every parent re-render.
   */
  onSearch?: (query: string) => void
  onResetView?: () => void
}

export function GraphToolbar({ filters, onChange, stats, searchValue, onSearch, onResetView }: GraphToolbarProps) {
  const [searchQuery, setSearchQuery] = useState(searchValue ?? '')
  // M4: skip initial mount to avoid overwriting URL-derived query state
  const didMountRef = useRef(false)
  // M3: stable ref for onSearch so debounce effect deps don't include the callback
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  // Sync when parent resets the search value (e.g. via Reset View)
  useEffect(() => { setSearchQuery(searchValue ?? '') }, [searchValue])

  // Live search: highlight nodes as user types (GH-128)
  // - Does not fire on initial mount (M4: preserves URL-derived query)
  // - Uses onSearchRef instead of onSearch in deps to avoid debounce resets (M3)
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    const timer = setTimeout(() => onSearchRef.current?.(searchQuery), 200)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const toggleType = (type: string) => {
    const current = filters.types ?? []
    const updated = current.includes(type) ? current.filter((t) => t !== type) : [...current, type]
    onChange({ ...filters, types: updated.length ? updated : undefined })
  }

  const toggleCategory = useCallback((cat: string) => {
    onChange({ ...filters, category: filters.category === cat ? undefined : cat })
  }, [filters, onChange])

  // M2: use onSearchRef.current for consistency with debounce path; deps don't include onSearch
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    onSearchRef.current?.(searchQuery)
  }, [searchQuery])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
    onSearchRef.current?.('')
  }, [])

  return (
    <div className="flex items-center gap-3 overflow-x-auto border-b border-white/[0.06] px-4 py-2.5 scrollbar-none">
      {/* Search input */}
      <form onSubmit={handleSearchSubmit} className="flex items-center gap-1.5">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Find node..."
            aria-label="Find graph node"
            className="h-8 w-36 rounded-md border border-white/[0.06] bg-surface-2 pl-7 pr-7 text-base md:text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-brand-500/40 focus:ring-1 focus:ring-brand-500/20"
          />
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500" aria-hidden="true" />
          {/* Clear button */}
          {searchQuery && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="absolute right-0 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-neutral-500 hover:text-neutral-300"
              aria-label="Clear graph search"
            >
              <X size={10} aria-hidden="true" />
            </button>
          )}
        </div>
      </form>

      {/* Separator */}
      <div className="h-4 w-px bg-white/[0.06]" />

      {/* Category filter chips (GH-128 Phase 1) */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500">Nodes:</span>
        {Object.entries(CATEGORY_COLORS).map(([cat, color]) => {
          // M2: chip is active only when explicitly selected; empty filter = no chip lit
          const active = filters.category === cat
          return (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              aria-pressed={active}
              aria-label={`Filter ${cat} nodes`}
              className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] transition-colors ${
                active
                  ? 'bg-surface-3 text-neutral-200'
                  : 'bg-transparent text-neutral-600 hover:text-neutral-400'
              }`}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
              {cat}
            </button>
          )
        })}
        {/* M2: active only when 'uncategorized' explicitly selected */}
        <button
          onClick={() => toggleCategory('uncategorized')}
          aria-pressed={filters.category === 'uncategorized'}
          aria-label="Filter uncategorized nodes"
          className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] transition-colors ${
            filters.category === 'uncategorized'
              ? 'bg-surface-3 text-neutral-200'
              : 'bg-transparent text-neutral-600 hover:text-neutral-400'
          }`}
        >
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: DEFAULT_NODE_COLOR }} />
          other
        </button>
      </div>

      {/* Separator */}
      <div className="h-4 w-px bg-white/[0.06]" />

      {/* Edge type filters */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500">Edges:</span>
        {EDGE_TYPES.map((type) => {
          const active = !filters.types?.length || filters.types.includes(type)
          return (
            <button
              key={type}
              onClick={() => toggleType(type)}
              aria-pressed={active}
              aria-label={`Toggle ${type} edges`}
              className={`rounded-md px-2 py-0.5 text-[11px] transition-colors ${
                active
                  ? 'bg-surface-3 text-neutral-200'
                  : 'bg-transparent text-neutral-600 hover:text-neutral-400'
              }`}
            >
              {type}
            </button>
          )
        })}
      </div>

      {/* Implicit edge toggle */}
      <label className="flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={filters.includeImplicit ?? true}
          onChange={(e) => onChange({ ...filters, includeImplicit: e.target.checked })}
          className="h-3 w-3 rounded border-neutral-600 bg-surface-2 text-brand-500 focus:ring-0"
        />
        <span className="text-[11px] text-neutral-400">Similarity</span>
      </label>

      {/* Reset view */}
      {onResetView && (
        <>
          <div className="h-4 w-px bg-white/[0.06]" />
          <button
            type="button"
            onClick={onResetView}
            className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-surface-3 hover:text-neutral-200"
            title="Reset view and clear focus"
            aria-label="Reset graph view"
          >
            <RotateCcw size={13} aria-hidden="true" />
          </button>
        </>
      )}

      {/* Stats */}
      {stats && (
        <div className="ml-auto flex items-center gap-3 text-[11px] text-neutral-500">
          <span>{stats.nodeCount} nodes</span>
          <span>{stats.explicitEdges} links</span>
          {stats.implicitEdges > 0 && <span>{stats.implicitEdges} similar</span>}
        </div>
      )}
    </div>
  )
}
